import React from 'react';
import {
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { SelectOption } from 'global';
import { DATE_RANGE_OPTIONS, GROUP_BY_OPTIONS, SUBMISSION_STAGE_OPTIONS } from '../../constants';
import { DateTypeToggle } from 'modules/finance/ui/components/DateTypeToggle';
import { CustomDateRangeInputs } from 'modules/finance/ui/components/CustomDateRangeInputs';
import { FinanceInsurancePerformanceReportGroupBy } from '@trnsact/trnsact-shared-types';
import { FinanceDateRange, FinancePaymentType, ClosedLostFiltersState, ClosedLostSubmissionStage } from '../../types';

interface Props {
  filters: ClosedLostFiltersState;
  onChange: (filters: ClosedLostFiltersState) => void;
  departmentOptions: SelectOption[];
  departmentOptionsLoading: boolean;
}

const FILTER_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
} as const;
const TOGGLE_BTN_SX = {
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 500,
  textTransform: 'none',
  border: 'none !important',
  borderRadius: '6px !important',
  '&.Mui-selected': { bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
} as const;
const TOGGLE_GROUP_SX = {
  bgcolor: 'action.hover',
  borderRadius: '8px',
  p: '3px',
  gap: 0,
  '& .MuiToggleButtonGroup-grouped': { m: 0 },
} as const;

export const ClosedLostFilters = ({ filters, onChange, departmentOptions, departmentOptionsLoading }: Props) => {
  const set = <K extends keyof ClosedLostFiltersState>(key: K, value: ClosedLostFiltersState[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <Stack spacing={1} mb={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Typography sx={FILTER_LABEL_SX}>Compare by</Typography>
        <Select
          size="small"
          value={filters.groupBy}
          onChange={(e: SelectChangeEvent) =>
            set('groupBy', e.target.value as FinanceInsurancePerformanceReportGroupBy)
          }
          sx={{ fontSize: 13, '& .MuiSelect-select': { py: '6px', px: '10px' } }}
        >
          {GROUP_BY_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        <Typography sx={{ ...FILTER_LABEL_SX, ml: 0.75 }}>Department</Typography>
        <Select
          size="small"
          value={filters.departmentId}
          disabled={departmentOptionsLoading || departmentOptions.length === 0}
          onChange={(e: SelectChangeEvent) => set('departmentId', e.target.value)}
          sx={{ fontSize: 13, '& .MuiSelect-select': { py: '6px', px: '10px' } }}
        >
          {departmentOptions.map(opt => (
            <MenuItem key={String(opt.value)} value={String(opt.value)} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        <DateTypeToggle value={filters.dateType} onChange={v => set('dateType', v)} />

        <Typography sx={{ ...FILTER_LABEL_SX, ml: 0.75 }}>Date</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
          {DATE_RANGE_OPTIONS.map(opt => (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => set('dateRange', opt.value as FinanceDateRange)}
              variant={filters.dateRange === opt.value ? 'filled' : 'outlined'}
              color={filters.dateRange === opt.value ? 'primary' : 'default'}
              sx={{ fontSize: 11, fontWeight: 500, height: 26, cursor: 'pointer' }}
            />
          ))}
          {filters.dateRange === 'custom' && (
            <CustomDateRangeInputs value={filters.customDateRange} onChange={v => set('customDateRange', v)} />
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={FILTER_LABEL_SX}>Submission</Typography>
          <ToggleButtonGroup
            exclusive
            value={filters.submissionStage}
            onChange={(_, v: ClosedLostSubmissionStage | null) => {
              if (v) set('submissionStage', v);
            }}
            sx={TOGGLE_GROUP_SX}
          >
            {SUBMISSION_STAGE_OPTIONS.map(opt => (
              <ToggleButton key={opt.value} value={opt.value} sx={TOGGLE_BTN_SX}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={FILTER_LABEL_SX}>Payment type</Typography>
          <ToggleButtonGroup
            exclusive
            value={filters.paymentType}
            onChange={(_, v: FinancePaymentType | null) => {
              if (v) set('paymentType', v);
            }}
            sx={TOGGLE_GROUP_SX}
          >
            <ToggleButton value="ALL" sx={TOGGLE_BTN_SX}>
              All
            </ToggleButton>
            <ToggleButton value="FINANCING" sx={TOGGLE_BTN_SX}>
              Financing
            </ToggleButton>
            <ToggleButton value="CASH" sx={TOGGLE_BTN_SX}>
              Cash
            </ToggleButton>
            <ToggleButton value="CREDIT_LINE" sx={TOGGLE_BTN_SX}>
              Credit Line
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Stack>
  );
};
